import { useEffect, useRef, useState } from 'react'
import { renderAsync } from 'docx-preview'
import { getWaterfallAgreement, getWaterfallAgreementMeta } from '@/services/waterfall'
import type { WaterfallAgreementMeta } from '@/types/waterfall'

interface WaterfallAgreementReaderProps {
  className?: string
  refreshKey?: number
  onMetaLoaded?: (meta: WaterfallAgreementMeta) => void
}

export default function WaterfallAgreementReader({
  className,
  refreshKey = 0,
  onMetaLoaded,
}: WaterfallAgreementReaderProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const styleRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [format, setFormat] = useState<'docx' | 'pdf' | null>(null)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const body = bodyRef.current
    const style = styleRef.current
    if (!body || !style) return

    body.innerHTML = ''
    style.innerHTML = ''
    setPdfUrl(null)
    setFormat(null)
    setStatus('loading')

    Promise.all([getWaterfallAgreementMeta(), getWaterfallAgreement()])
      .then(async ([meta, blob]) => {
        if (cancelled) return
        onMetaLoaded?.(meta)
        setFormat(meta.format)

        if (meta.format === 'pdf') {
          objectUrl = URL.createObjectURL(blob)
          setPdfUrl(objectUrl)
          setStatus('ready')
          return
        }

        await renderAsync(blob, body, style, {
          className: 'docx-agreement',
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: true,
          breakPages: false,
        })
        if (!cancelled) setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [refreshKey, onMetaLoaded])

  return (
    <div className={className}>
      <div ref={styleRef} className="sr-only" aria-hidden="true" />
      {status === 'loading' && (
        <p className="text-sm text-gray-500">Loading agreement…</p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-600">Could not load the agreement document.</p>
      )}
      {format === 'pdf' && pdfUrl && status === 'ready' && (
        <iframe
          src={pdfUrl}
          title="Distribution waterfall agreement"
          className="h-[calc(100vh-12rem)] min-h-[20rem] w-full rounded border-0 bg-white max-md:min-h-[16rem]"
        />
      )}
      <div
        ref={bodyRef}
        className={[
          'docx-agreement-host min-w-0 [&_.docx-agreement-wrapper]:!bg-white',
          format === 'docx' && status === 'ready' ? 'block' : 'hidden',
        ].join(' ')}
      />
    </div>
  )
}
